// Zod schemas for every entity and every RPC input/output.
// These are the wire contract: the API validates against them, the client infers from them.

import { z } from 'zod';
import { ACTIVITY_TYPES, BILLING_IDS, LEAD_STATUS_IDS } from './constants';

export const statusSchema = z.enum(LEAD_STATUS_IDS);
export const billingSchema = z.enum(BILLING_IDS);
export const activityTypeSchema = z.enum(ACTIVITY_TYPES);

// ── Entities ─────────────────────────────────────────────────────────────────

export const planSchema = z.object({
  id: z.string(),
  appId: z.string(),
  name: z.string(),
  price: z.number(),
  sortOrder: z.number(),
});

export const appSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.date(),
});

export const appWithPlansSchema = appSchema.extend({
  plans: z.array(planSchema),
  leadCount: z.number(),
});

export const campaignSchema = z.object({
  id: z.string(),
  name: z.string(),
  spend: z.number(),
});

export const settingsSchema = z.object({
  hourlyRate: z.number(),
  emailMinutes: z.number(),
});

export const researchSchema = z.object({
  id: z.string(),
  appId: z.string(),
  company: z.string(),
  phone: z.string(),
  email: z.string(),
  www: z.string(),
  note: z.string(),
  createdAt: z.date(),
});

export const activitySchema = z.object({
  id: z.string(),
  leadId: z.string(),
  type: activityTypeSchema,
  at: z.date(),
  durationSec: z.number().nullable(),
  outcome: z.string().nullable(),
  note: z.string(),
});

export const leadSchema = z.object({
  id: z.string(),
  appId: z.string(),
  company: z.string(),
  contact: z.string(),
  role: z.string(),
  phone: z.string(),
  email: z.string(),
  status: statusSchema,
  planId: z.string().nullable(),
  billing: billingSchema,
  sourceId: z.string().nullable(),
  createdAt: z.date(),
  subStart: z.date().nullable(),
});

export const leadEconSchema = z.object({
  callCount: z.number(),
  callSec: z.number(),
  emails: z.number(),
  timeCost: z.number(),
  adCost: z.number(),
  cac: z.number(),
  mrr: z.number(),
  months: z.number(),
  revenue: z.number(),
  ret: z.number().nullable(),
  payback: z.number().nullable(),
});

/** Lead row enriched with computed economics + the resolved plan/campaign names (pipeline table). */
export const leadWithEconSchema = leadSchema.extend({
  econ: leadEconSchema,
  planName: z.string().nullable(),
  planPrice: z.number().nullable(),
  campaignName: z.string().nullable(),
  lastActivityAt: z.date(),
});

/** Full lead detail for the drawer / calling context. */
export const leadDetailSchema = leadWithEconSchema.extend({
  activities: z.array(activitySchema),
});

// ── Dashboard / costs aggregates ───────────────────────────────────────────────

export const dashboardSummarySchema = z.object({
  kpis: z.object({
    mrr: z.number(),
    avgCac: z.number(),
    totalRet: z.number(),
    callCount: z.number(),
    callSec: z.number(),
    conversion: z.number(),
    subCount: z.number(),
    closedCount: z.number(),
    leadCount: z.number(),
    newCount: z.number(),
  }),
  funnel: z.array(
    z.object({ id: z.string(), label: z.string(), color: z.string(), count: z.number() }),
  ),
  recent: z.array(
    z.object({
      activity: activitySchema,
      leadId: z.string(),
      company: z.string(),
    }),
  ),
  subs: z.array(leadWithEconSchema),
});

export const costsSummarySchema = z.object({
  settings: settingsSchema,
  callCount: z.number(),
  callSec: z.number(),
  callCost: z.number(),
  emails: z.number(),
  mailCost: z.number(),
  adTotal: z.number(),
  totalCost: z.number(),
  subCount: z.number(),
  blendedCac: z.number(),
  campaigns: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      spend: z.number(),
      leadCount: z.number(),
      subCount: z.number(),
      costPerLead: z.number().nullable(),
      costPerSub: z.number().nullable(),
    }),
  ),
});

// ── Inputs ────────────────────────────────────────────────────────────────────

export const appIdInput = z.object({ appId: z.string().min(1) });
export const idInput = z.object({ id: z.string().min(1) });

export const createAppInput = z.object({ name: z.string().trim().min(1).max(120) });

export const updateSettingsInput = z.object({
  hourlyRate: z.number().min(0).optional(),
  emailMinutes: z.number().min(0).optional(),
});

export const createCampaignInput = z.object({ name: z.string().trim().min(1).max(120) });
export const updateCampaignSpendInput = z.object({ id: z.string().min(1), spend: z.number().min(0) });

export const createResearchInput = z.object({
  appId: z.string().min(1),
  company: z.string().trim().min(1).max(200),
  phone: z.string().trim().max(60).default(''),
  email: z.string().trim().max(200).default(''),
  www: z.string().trim().max(200).default(''),
  note: z.string().trim().max(500).default(''),
});

export const bulkCreateResearchInput = z.object({
  appId: z.string().min(1),
  items: z
    .array(
      z.object({
        company: z.string().trim().min(1),
        phone: z.string().trim().default(''),
        email: z.string().trim().default(''),
        www: z.string().trim().default(''),
      }),
    )
    .min(1)
    .max(500),
});

export const updateResearchNoteInput = z.object({ id: z.string().min(1), note: z.string().max(500) });

export const promoteResearchInput = z.object({ id: z.string().min(1), andCall: z.boolean().default(false) });

export const createLeadInput = z.object({
  appId: z.string().min(1),
  company: z.string().trim().min(1).max(200),
  contact: z.string().trim().max(160).default(''),
  role: z.string().trim().max(160).default(''),
  phone: z.string().trim().max(60).default(''),
  email: z.string().trim().max(200).default(''),
  status: statusSchema.default('nowy'),
  planId: z.string().nullable().default(null),
  billing: billingSchema.default('monthly'),
  sourceId: z.string().nullable().default(null),
});

export const updateLeadInput = z.object({
  id: z.string().min(1),
  patch: z
    .object({
      company: z.string().trim().min(1).max(200).optional(),
      contact: z.string().trim().max(160).optional(),
      role: z.string().trim().max(160).optional(),
      phone: z.string().trim().max(60).optional(),
      email: z.string().trim().max(200).optional(),
      status: statusSchema.optional(),
      planId: z.string().nullable().optional(),
      billing: billingSchema.optional(),
      sourceId: z.string().nullable().optional(),
    })
    .refine((p) => Object.keys(p).length > 0, { message: 'Brak zmian do zapisania' }),
});

export const addActivityInput = z.object({
  id: z.string().min(1),
  type: z.enum(['note', 'email']),
  note: z.string().max(2000).default(''),
});

export const logCallInput = z.object({
  id: z.string().min(1),
  durationSec: z.number().int().min(0),
  outcome: z.enum(['no_answer', 'callback', 'demo', 'interested', 'rejected', 'sub']),
  note: z.string().max(2000).default(''),
});

export const okSchema = z.object({ ok: z.boolean() });
